<template>
  <ResponsiveDialog
    ref="dialog"
    :variant="maxSmall ? 'auto' : 'slideshow'"
    :open="open"
    :title="t('components.share_task.supporting_images.title')"
    :description="t('components.share_task.supporting_images.description')"
    headerVariant="slideshow"
    @close="$emit('close')"
  >
    <template v-if="$slots.default" #trigger>
      <slot />
    </template>

    <template #content>
      <div class="relative">
        <div
          class="flex flex-col items-center justify-center overflow-hidden rounded-md"
        >
          <Transition appear v-show="loaded" name="fade" mode="out-in">
            <video
              v-if="images[currentIndex]?.mime_type.includes('video')"
              :key="currentIndex"
              controls
              :src="images[currentIndex]?.url"
              class="aspect-square object-cover"
              @loadedmetadata="handleImageLoaded(images[currentIndex]?.url)"
            />
            <img
              v-else
              :key="currentIndex"
              :src="images[currentIndex]?.url"
              alt=""
              @load="handleImageLoaded(images[currentIndex]?.url)"
            />
          </Transition>
          <LoadingSpinner v-show="!loaded" class="!w-16 !h-16" />
        </div>
        <template v-if="loaded">
          <template v-if="images.length > 1">
            <div
              class="group cursor-pointer flex items-center justify-center h-9 w-9 bg-alpha-white-90 backdrop-blur-sm rounded-full absolute left-4 top-1/2 -translate-y-1/2"
            >
              <ChevronLeft
                class="transition-colors w-6 h-6 text-utility-gray-700 group-hover:text-utility-gray-500"
                @click="previous"
              />
            </div>
            <div
              class="group cursor-pointer flex items-center justify-center h-9 w-9 bg-alpha-white-90 backdrop-blur-sm rounded-full absolute right-4 top-1/2 -translate-y-1/2"
            >
              <ChevronRight
                class="transition-colors w-5 h-5 text-utility-gray-700 group-hover:text-utility-gray-500"
                @click="next"
              />
            </div>
          </template>
        </template>
      </div>
    </template>
  </ResponsiveDialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { onKeyStroke } from "@vueuse/core";
import ResponsiveDialog from "../Dialog/ResponsiveDialog.vue";
import { useI18n } from "vue-i18n";
import { LoadingSpinner } from "@/Components";
import { ChevronLeft, ChevronRight } from "@/Icons";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const { t } = useI18n();
const { maxSmall } = useBreakpoints();

defineEmits(["close"]);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  initialIndex: { type: [Number, String], required: false },
  images: { type: Array, required: true },
});

const loadedImages = ref(new Map());
const currentIndex = ref(props.initialIndex || 0);
const currentImage = computed(() => {
  return props.images[currentIndex.value]?.url;
});

watch(
  () => props.initialIndex,
  () => {
    currentIndex.value = props.initialIndex;
  }
);

const loaded = computed(() => {
  return loadedImages.value.get(currentImage.value) ? true : false;
});

function handleImageLoaded(imageUrl) {
  loadedImages.value.set(imageUrl, true);
}

onKeyStroke(["ArrowLeft"], () => {
  previous();
});

onKeyStroke(["ArrowRight"], () => {
  next();
});

function previous() {
  if (currentIndex.value - 1 >= 0) {
    currentIndex.value--;
  } else {
    currentIndex.value = props.images.length - 1;
  }
}

function next() {
  if (currentIndex.value + 1 < props.images.length) {
    currentIndex.value++;
  } else {
    currentIndex.value = 0;
  }
}
</script>

<style lang="scss" scoped>
.control {
  @apply absolute top-1/2 w-8 rounded-full mx-4 text-white bg-gray-300 sm:bg-transparent hover:bg-gray-300 bg-opacity-40 hover:bg-opacity-40 cursor-pointer;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.1s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 50%;
}
</style>
